import { useEffect, useRef, useState } from "react";

import { Key, User } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { useWorkspaceSlug } from "@/components/lib/use-workspace-slug";
import { WorkspaceLogo } from "@/components/pages_public/WorkspaceLogo";
import { setTokens } from "@/components/session/JwtTokenManager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SITE_NAME } from "@/css/constants";
import {
  useActivateWorkspaceMembershipMutation,
  useRefreshTokenMutation,
} from "@/graphql";
import { cn } from "@/lib/utils";
import { getBrowserTimezone } from "@/utils/datetime";

interface ActivateMembershipProps {
  title?: React.ReactNode;
  activeText?: string;
  marginTop?: number;
  redirectUrl?: string;
}

export function ActivateMembershipPage({
  title,
  activeText = "Activate Account",
  marginTop = 5,
  redirectUrl = "/dashboard",
}: ActivateMembershipProps) {
  const [redirecting, setRedirecting] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const [name, setName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>();
  const { refreshToken } = useParams();
  const workspaceSlug = useWorkspaceSlug();
  const navigate = useNavigate();

  function onError() {
    setError(
      "Something went wrong and we were unable to activate your account. Please try again later.",
    );
  }

  const [activate, { loading }] = useActivateWorkspaceMembershipMutation({
    onCompleted: (data) => {
      const { ok, sesameQueryParam } = data?.activateWorkspaceMembership ?? {};

      if (!ok) {
        onError();
        return;
      }

      setRedirecting(true);

      if (USE_SUBDOMAINS) {
        window.location.replace(`${redirectUrl}${sesameQueryParam}`);
      } else {
        navigate(redirectUrl, { replace: true });
      }
    },
    onError,
  });

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (name && password && workspaceSlug) {
      activate({
        variables: {
          name,
          password,
          workspace: workspaceSlug,
          timeZone: getBrowserTimezone(),
        },
      });
    } else {
      setError("Please enter your name and password");
    }
  };

  const defaultTitle = (
    <div className="text-2xl font-semibold">Activate Account</div>
  );

  return (
    <>
      <SEOHead title={`Activate your ${SITE_NAME} account`} />
      <AuthenticateActivation
        refreshToken={refreshToken ?? ""}
        workspaceSlug={workspaceSlug ?? ""}
        onAuthExpired={() => setAuthExpired(true)}
      />
      <div
        className={cn("flex justify-center px-4", marginTop === 5 && "mt-20")}
      >
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <WorkspaceLogo />
          </div>
          <div className="mb-8 text-center">{title || defaultTitle}</div>

          {authExpired ? (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  The link you visited is either expired or never existed. Try
                  resetting your password again?
                </AlertDescription>
              </Alert>
              <Link
                to={`/forgot-password${
                  import.meta.env.SSR ? "" : window.location.search
                }`}
              >
                <Button className="w-full">Reset Password</Button>
              </Link>
            </div>
          ) : (
            <Card>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="George Washington"
                      value={name}
                      name="name"
                      onChange={(e) => {
                        setName(e.target.value);
                        setError(undefined);
                      }}
                      disabled={loading}
                      onKeyDown={handleKeyPress}
                      autoFocus={true}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Type your new password..."
                      value={password}
                      name="password"
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(undefined);
                      }}
                      disabled={loading}
                      onKeyDown={handleKeyPress}
                      className="pl-10"
                    />
                  </div>
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={loading || redirecting}
                  className="w-full"
                >
                  {loading || redirecting ? "Loading..." : activeText}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

interface AuthenticateActivationProps {
  refreshToken: string;
  workspaceSlug: string;
  onAuthExpired: () => void;
}

function AuthenticateActivation({
  refreshToken,
  workspaceSlug,
  onAuthExpired,
}: AuthenticateActivationProps) {
  const hasRun = useRef(false);

  const [authenticate] = useRefreshTokenMutation({
    variables: {
      refreshToken,
    },
    onCompleted: (data) => {
      const { token, refreshToken } = data.refreshToken ?? {};
      setTokens({
        token: token ?? null,
        refreshToken: refreshToken ?? null,
        oneTimeUse: USE_SUBDOMAINS,
        workspaceSlug,
      });
    },
    onError: onAuthExpired,
  });

  useEffect(() => {
    if (refreshToken && !hasRun.current) {
      hasRun.current = true;
      authenticate();
    }
    return () => {
      if (!USE_SUBDOMAINS) return;

      setTokens({
        token: null,
        refreshToken: null,
      });
    };
  }, [refreshToken]);

  return null;
}
